import { useGetMainOccupationList } from "@/api/services/global/master";
import { useGetPrefectureAndMunicipalityByZipCode } from "@/api/services/global/zip-code";
import { checkDuplicateEmployeeFieldsApi, createEmployeeApi } from "@/api/services/main/employee";
import { TCheckDuplicateEmployeeFieldsOutputDto } from "@/api/services/main/employee/dtos/check-duplicate-employee-fields.dto";
import { ConfirmSaveModal } from "@/components/commons/ConfirmSaveModal";
import { Form } from "@/components/elements/Form";
import { TopPage } from "@/components/elements/TopPage";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { CheckDuplicateEmployeeFieldsModal } from "@/features/employee/components/CheckDuplicateEmployeeFieldsModal";
import { EmployeeForm } from "@/features/employee/components/employee-forms";
import { EmployeeFormBtnActions } from "@/features/employee/components/EmployeeFormBtnActions";
import { useGetEmployeeSchema } from "@/features/employee/hook/useGetEmployeeSchema";
import { TEmployeeForm } from "@/features/employee/types/employeeForm";
import { convertEmployeeFormDataToReqDto } from "@/features/employee/utils/convertFormDataToReqDto";
import { useDialog } from "@/hooks/useDialog";
import { AppRoutes } from "@/routes/config";
import { background } from "@/theme/colors";
import { EMPLOYEE_TRAINING_STATUS, EMPLOYMENT_STATUS, RETIREMENT_STATUS } from "@/types/enum";
import { setErrorForm, showError } from "@/utils/error";
import { getFormValues } from "@/utils/pieces";
import { toast } from "@/utils/toast";
import { getDynamicRoute } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, styled, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { useNavigate } from "react-router-dom";

type TEmployeeCreatePageProps = {};

export const EmployeeCreatePage: FC<TEmployeeCreatePageProps> = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: mainOccupation } = useGetMainOccupationList();

  const [existingDuplicateEmployeeFields, setExistingDuplicateEmployeeFields] = useState<TCheckDuplicateEmployeeFieldsOutputDto[]>();

  const confirmSave = useDialog();

  const { employeeSchema, employeeDraftSchema } = useGetEmployeeSchema();
  const [enableBlocker, setEnableBlocker] = useState<boolean>(true);

  const [confirmMessage, setConfirmMessage] = useState<string[]>([]);

  const methods = useForm<TEmployeeForm>({
    resolver: yupResolver(employeeSchema),
    mode: "all",
    reValidateMode: "onBlur",
    defaultValues: {
      residenceStatusId: "",
      retirementStatus: RETIREMENT_STATUS.ENROLLMENT,
      employmentStatus: EMPLOYMENT_STATUS.OFFERED,
      careerTrainingStatus: EMPLOYEE_TRAINING_STATUS.UN_SELECTED,
      contractMainOccupationId: mainOccupation.find((item) => item.mainOccupation === "その他")?.id.toString() ?? "",
      hasRetiredBefore: false,
    },
  });

  const zipCodeWatchField = useWatch({ control: methods.control, name: "zipCode" });
  const { isFetching: isFetchingPrefectureAndMunicipality } = useGetPrefectureAndMunicipalityByZipCode(zipCodeWatchField ?? "");

  const saveDataForm = async (data: TEmployeeForm, isDraft: boolean) => {
    if (isFetchingPrefectureAndMunicipality) return;
    try {
      const payload = convertEmployeeFormDataToReqDto(data, isDraft);

      if (!!payload.residenceCode || !!payload.managementNumber || !!payload.passportCode) {
        const existing = await checkDuplicateEmployeeFieldsApi({
          employeeId: null,
          residenceCode: payload.residenceCode,
          managementNumber: payload.managementNumber,
          passportCode: payload.passportCode,
        });

        if (existing.data.length > 0) {
          setExistingDuplicateEmployeeFields(existing.data);
          return;
        }
      }

      const rs = await createEmployeeApi(payload);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EMPLOYEE.FETCH_LIST] });
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EMPLOYEE.FETCH_DETAIL, rs.data.id] });
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);

      if (isDraft) {
        navigate(AppRoutes.employeeList);
      } else {
        navigate(getDynamicRoute(AppRoutes.employeeEdit, { id: rs.data.id }));
      }
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  const onSubmit = async (data: TEmployeeForm) => {
    await saveDataForm(data, false);
  };

  const saveDraft = async () => {
    const data = methods.getValues();
    const fields = [];
    if (!data.firstName) {
      fields.push("・名");
    }
    if (!data.employmentStatus) {
      fields.push("・在籍ステータス");
    }
    if (fields.length > 0) {
      toast.error(`下書き保存のためには、以下の項目を事前にご登録ください。\n${fields.join("\n")}`);
      return;
    }

    let confirm = true;
    setConfirmMessage(["未入力項目があるため、下書き状態となり、申請は行えませんがよろしいですか？"]);
    confirm = await confirmSave.show();

    if (!confirm) return;

    try {
      setEnableBlocker(false);
      const values = await getFormValues(data, employeeDraftSchema);
      await saveDataForm(values as TEmployeeForm, true);
    } catch (error) {
      setEnableBlocker(true);
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <>
      <Form methods={methods} onSubmit={onSubmit} schema={employeeSchema} enableBlocker={enableBlocker}>
        <TopPageWrapper>
          <TopPage title="所属する外国人" backUrl={AppRoutes.employeeList} rightContent={<EmployeeFormBtnActions saveDraft={saveDraft} />} />
        </TopPageWrapper>
        <EmployeeForm />

        <CheckDuplicateEmployeeFieldsModal
          existingDuplicateEmployeeFields={existingDuplicateEmployeeFields}
          onClose={() => setExistingDuplicateEmployeeFields(undefined)}
        />
      </Form>

      <ConfirmSaveModal open={confirmSave.open} onOk={confirmSave.confirm} onClose={confirmSave.cancel}>
        {confirmMessage.map((message, index) => (
          <Typography component="p" variant="subtitle1" key={index}>
            {message}
          </Typography>
        ))}
      </ConfirmSaveModal>
    </>
  );
};

const TopPageWrapper = styled(Box)`
  position: sticky;
  top: 80px;
  z-index: 100;
  background-color: ${background.primary};
  padding-top: 24px;
  transform: translateY(-24px);
`;
